import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import { Node, Process, ProcessPdmPrefix, User } from '@prisma/client';
import { Breadcrumb, Button, Input, Layout, Modal, Table } from 'antd';

import { Role } from '@app/domain-interfaces';
import { Auth, PageLayout, Tables } from '@app/web-components';

import { CreatedByColumn, CreatedOnColumn, UpdatedByColumn, UpdatedOnColumn } from '../../../../../common';
import { useFindProcesses } from '../data';
import { useProcessActions } from '../libs/use-process-actions';

export type LandingPageItem = Process & {
  t3Node?: Node;
  pdmPrefixes: ProcessPdmPrefix[];
  createdByUser: User;
  updatedByUser: User;
};

export const LandingPage = () => {
  const { user } = Auth.useUserAuth();
  const { getActionButton, getDeleteModalProps, getDetailsModalProps } = useProcessActions();

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<LandingPageItem>(
    'Process',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => getActionButton(record),
      },
      {
        title: 'DMS Process Name',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: 'PDM Prefixes',
        dataIndex: ['pdmPrefixes'],
        render: (value: ProcessPdmPrefix[]) => value.map(({ prefix }) => prefix).join(', '),
        filterDropdown: Tables.customSearchFilterDropdownFactory('Search PDM prefixes'),
      },
      {
        title: 'T3',
        dataIndex: ['t3Node', 'name'],
        sorter: true,
      },
      UpdatedByColumn,
      UpdatedOnColumn,
      CreatedByColumn,
      {
        ...CreatedOnColumn,
        defaultSortOrder: 'descend',
      },
    ],
    {
      customFilters: {
        pdmPrefixes: (value) => ({
          pdmPrefixes: { some: { prefix: { contains: value, mode: 'insensitive' } } },
        }),
      },
      globalSearchFields: ['name', 't3Node.name'],
    },
  );

  const queryResult = useFindProcesses<LandingPageItem>(
    getQuery({
      include: { t3Node: true, pdmPrefixes: true, createdByUser: true, updatedByUser: true },
    }),
  );

  return (
    <>
      <Modal {...getDeleteModalProps()} />
      <Modal {...getDetailsModalProps()} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Processes' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Processes" />

        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              user?.roles[Role.RULE_ADMIN] ? <Button {...getCreateButtonProps()} /> : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink
                {...getCSVLinkProps(queryResult.data?.data, {
                  pdmPrefixes: (value: ProcessPdmPrefix[]) => value.map(({ prefix }) => prefix).join(', '),
                })}
              />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
